import React from 'react'
import LazyHydrate from 'react-lazy-hydration'
import { GuildHighResShieldIcon } from '@/atoms/Icons/GuildHighResShieldIcon'
import { CaptionSM, EyebrowLG, HeadingMD, HeadingXL, ParagraphLG } from '@/atoms/Text'
import { Collection } from '@/types/codegen-contentful'
import { useTranslate } from '@/utils/translate/translate-client'
import { ResponsiveBackgroundImage } from '../../atoms/ResponsiveBackgroundImage'

interface GuildMembershipBannerProps {
  guildMembershipNumbers: Collection
  isGuildMember: boolean
}

export const GuildMembershipBanner: React.FC<GuildMembershipBannerProps> = ({
  guildMembershipNumbers,
  isGuildMember,
}) => {
  const { t } = useTranslate('common')

  const numSubscribers = guildMembershipNumbers?.customData[0]?.numSubscribers
  const dateUpdated = guildMembershipNumbers?.customData[0]?.updatedAt

  const cloudinaryImgPaths = {
    mobile: 'https://images.angelstudios.com/image/upload/v1725160539/angel-studios/landing/invest/Mobile_-_428px.webp',
    tablet: 'https://images.angelstudios.com/image/upload/v1725160489/angel-studios/landing/invest/Tablet_-_834px.webp',
    desktop:
      'https://images.angelstudios.com/image/upload/v1725160502/angel-studios/landing/invest/Desktop_-_1728px.webp',
  }

  return (
    <div className="relative">
      <ResponsiveBackgroundImage
        src={cloudinaryImgPaths.desktop}
        srcUrls={{
          xs: cloudinaryImgPaths.mobile,
          sm: cloudinaryImgPaths.mobile,
          md: cloudinaryImgPaths.tablet,
          lg: cloudinaryImgPaths.desktop,
          xl: cloudinaryImgPaths.desktop,
        }}
      />
      <LazyHydrate whenVisible>
        <div className="relative flex flex-col items-center gap-8 px-4 pb-10 pt-40 text-center md:px-16 lg:pb-14">
          <GuildHighResShieldIcon />
          <div className="flex flex-col items-center gap-4">
            <HeadingMD weight="bold" className="md:photon-display md:max-w-[600px]">
              {isGuildMember
                ? t('youAreAPart', 'You Are a Part of Something Big')
                : t('joinTheAngelGuildCommunity', 'Join the Angel Guild Community')}
            </HeadingMD>
            <ParagraphLG className="md:max-w-[520px]" color="core-gray-400" weight="medium">
              {t(
                'ourGuildCommunityPayingMembersInvestors',
                'Our Guild community includes paying members and investors in Angel Studios projects.',
              )}
            </ParagraphLG>
            <div className="mt-8 flex flex-col gap-2">
              <EyebrowLG className="uppercase" color="core-gray-400">
                {t('activePayingGuildMembers', 'Active Paying Guild Members')}
              </EyebrowLG>
              <HeadingXL weight="bold" className="capitalize">
                {numSubscribers}
              </HeadingXL>
            </div>
            <CaptionSM className="mt-[72px]" color="core-gray-400">
              {t('lastUpdated', 'Last updated')} {dateUpdated}
            </CaptionSM>
          </div>
        </div>
      </LazyHydrate>
    </div>
  )
}
